import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Label,
  Modal,
  Spinner,
  Textarea,
  TextInput,
  ToggleSwitch,
} from 'flowbite-react'
import { HiExclamationCircle } from 'react-icons/hi'
import { getApiErrorMessage } from '../utils/apiError'
import { isValidSlug } from '../utils/departments'

const blankForm = { name: '', description: '', active: true, is_catch_all: false, slug: '' }

const toForm = (entity) =>
  entity
    ? {
        name: entity.name ?? '',
        description: entity.description ?? '',
        active: Boolean(entity.active),
        is_catch_all: Boolean(entity.is_catch_all),
        slug: entity.slug ?? '',
      }
    : { ...blankForm }

/**
 * Shared create/edit form for a department or an AoR — they share an identical
 * field set. `onSubmit(form)` performs the API call and updates parent state; it
 * should throw on failure so this modal can surface the server message (e.g. a
 * 409 duplicate-slug conflict) inline. The parent closes the modal on success.
 */
const EntityFormModal = ({
  open,
  mode = 'create',
  entityLabel = 'Department',
  catchAllHint = 'Routes reports that match nothing more specific. Only one per parent — setting this clears it on the current catch-all.',
  initial = null,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState(blankForm)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setForm(toForm(initial))
      setShowAdvanced(Boolean(initial?.slug))
      setError('')
      setSubmitting(false)
    }
  }, [open, initial])

  const update = (patch) => setForm((current) => ({ ...current, ...patch }))

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')

    if (!form.name.trim()) {
      setError('Name is required.')
      return
    }
    const slug = form.slug.trim()
    if (slug && !isValidSlug(slug)) {
      setError('Slug may contain only lowercase letters, numbers, and underscores.')
      return
    }

    setSubmitting(true)
    try {
      await onSubmit(form)
      // Parent hides the modal (open=false) on success.
    } catch (err) {
      setError(getApiErrorMessage(err, `Failed to save ${entityLabel.toLowerCase()}`))
      setSubmitting(false)
    }
  }

  const title = `${mode === 'edit' ? 'Edit' : 'New'} ${entityLabel}`

  return (
    <Modal show={open} onClose={submitting ? undefined : onClose} size="lg">
      <Modal.Header>{title}</Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="space-y-4">
            {error && (
              <Alert color="failure" icon={HiExclamationCircle}>
                {error}
              </Alert>
            )}

            <div>
              <Label htmlFor="entity-name" className="mb-1 block">
                Name *
              </Label>
              <TextInput
                id="entity-name"
                value={form.name}
                maxLength={150}
                required
                autoFocus
                onChange={(event) => update({ name: event.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="entity-description" className="mb-1 block">
                Description
              </Label>
              <Textarea
                id="entity-description"
                rows={3}
                value={form.description}
                onChange={(event) => update({ description: event.target.value })}
              />
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:gap-8">
              <ToggleSwitch
                checked={form.active}
                label="Active"
                onChange={(checked) => update({ active: checked })}
              />
              <ToggleSwitch
                checked={form.is_catch_all}
                label="Catch-all"
                onChange={(checked) => update({ is_catch_all: checked })}
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{catchAllHint}</p>

            <div>
              <button
                type="button"
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                onClick={() => setShowAdvanced((value) => !value)}
              >
                {showAdvanced ? 'Hide advanced' : 'Advanced'}
              </button>
              {showAdvanced && (
                <div className="mt-2">
                  <Label htmlFor="entity-slug" className="mb-1 block">
                    Slug
                  </Label>
                  <TextInput
                    id="entity-slug"
                    value={form.slug}
                    maxLength={100}
                    placeholder="Leave blank to generate from the name"
                    onChange={(event) => update({ slug: event.target.value })}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Lowercase letters, numbers, and underscores only. Must be unique within
                    its parent.
                  </p>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex w-full items-center justify-end gap-2">
            <Button color="gray" type="button" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner size="sm" className="mr-2" />}
              {mode === 'edit' ? 'Save Changes' : `Create ${entityLabel}`}
            </Button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default EntityFormModal
